import { Config } from '@browsing/config';
import { FashionPreferencesGenderValues } from '@shared/types/fashion-preferences';

export function timeToArray(millis: number): number[] {
    const seconds = millis / 1000;
    const m = Math.floor(seconds / 60);
    const h = Math.floor(m / 60);
    const d = Math.floor(h / 24);

    return [d, h % 24, m % 60, Math.floor(seconds % 60)];
}

export function getPixelRatio(): number {
    return window.devicePixelRatio;
}

export function isTouchScreen(): boolean {
    return (
        typeof navigator !== 'undefined' &&
        ('ontouchstart' in window ||
            navigator.maxTouchPoints > 0 ||
            (navigator as any).msMaxTouchPoints > 0)
    );
}

export function isLandscapeAndTouchScreen(): boolean {
    return isTouchScreen() && window.innerWidth > window.innerHeight;
}

export function getCurrencyCode(appDomain: number): string {
    let currencyCode = 'EUR';
    switch (appDomain) {
        case 40:
        case 41:
            currencyCode = 'CHF';
            break;
        case 43:
            currencyCode = 'GBP';
            break;
        case 45:
            currencyCode = 'PLN';
            break;
        case 51:
            currencyCode = 'CZK';
            break;
        case 63:
            currencyCode = 'NOK';
            break;
        default:
            break;
    }

    return currencyCode;
}

export function capitalize(string?: string): string {
    if (typeof string !== 'string') {
        return '';
    }

    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function flatten(data: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    function recurse(cur: Record<string, unknown>, prop: string) {
        if (Object(cur) !== cur) {
            result[prop] = cur;
        } else {
            // eslint-disable-next-line no-lonely-if
            if (Array.isArray(cur)) {
                const len = cur.length;
                for (let i = 0; i < len; i++) {
                    recurse(cur[i], `${prop}[${i}]`);
                }
                if (len === 0) {
                    result[prop] = [];
                }
            } else {
                let isEmpty = true;
                // eslint-disable-next-line guard-for-in
                for (const p in cur) {
                    isEmpty = false;
                    recurse(cur[p] as Record<string, unknown>, prop ? `${prop}.${p}` : p);
                }
                if (isEmpty && prop) {
                    result[prop] = {};
                }
            }
        }
    }
    recurse(data, '');

    return result;
}

export function isUpcomingEnabled({ isEnabled }: Config): boolean {
    return isEnabled('upcoming');
}

export function makeLoopFunction(func: Function, ms: number) {
    let id: number;

    return {
        start() {
            id = setInterval(func, ms);
        },
        stop() {
            clearInterval(id);
        },
    };
}

export function visibleOnce(payload: unknown, callback: Function) {
    let isVisibleOnce = false;

    return (isVisible: boolean) => {
        if (!isVisibleOnce && isVisible) {
            isVisibleOnce = true;
            callback(payload);
        }
    };
}

interface ScrollToOptions {
    top?: number;
    left?: number;
    behavior?: 'smooth' | 'auto';
}

export const scrollTo = ({ behavior = 'smooth', left = 0, top = 0 }: ScrollToOptions) => {
    try {
        return window.scrollTo({
            behavior,
            left,
            top,
        });
    } catch (e) {
        return window.scrollTo(left, top);
    }
};

export function extractError(...errorLists: Array<Array<unknown>>) {
    if (errorLists.length === 0) {
        return null;
    }

    const totalErrors = errorLists.reduce(
        (result, errors) => result.concat(Array.isArray(errors) ? errors : []),
        [],
    );

    if (totalErrors.length === 0) {
        return null;
    }

    return {
        error: totalErrors.pop(),
    };
}

export function isMoreThanDaysAgo(dateString: string) {
    // thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000 = 2592000000;
    const timestampThirtyDaysAgo = new Date().getTime() - 2592000000;
    const uspDate = new Date(dateString).getTime();

    return uspDate > timestampThirtyDaysAgo;
}

export const toNumberOrZero = (s: string) => {
    const i = +s;
    if (Number.isNaN(i)) {
        return 0;
    }
    return i;
};

export const incrementPageVisitsCount = (fieldName: string): number => {
    try {
        const visits = toNumberOrZero(window.localStorage.getItem(fieldName));
        window.localStorage.setItem(fieldName, (visits + 1).toString());
        return visits + 1;
    } catch (e) {
        console.error(e);
    }
};

export const readSelectedFashionPreference = ():
    | FashionPreferencesGenderValues
    | 'FEMALE'
    | 'MALE'
    | undefined => {
    try {
        return (
            (sessionStorage.getItem(
                'selected_fashion_preference',
            ) as FashionPreferencesGenderValues) || undefined
        );
    } catch (error) {
        return undefined;
    }
};
